import { flyingTulipAssetsFor, flyingTulipConfigFor } from '@1delta/data-sdk'
import { FlyingTulipLendingLensAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { FLYING_TULIP_LENDER_KEY } from '../../../lending/public-data/flying-tulip/convertPublic'

/**
 * Flying Tulip oracle price fetcher.
 *
 * The protocol liquidates against its own `OracleRouter`, read through the
 * `LendingLens` — `priceAndDecimals(asset)` returns a DIRECT USD price
 * (1e18-scaled), so entries belong in PASS 1 and feed the shared `usdPrices`
 * map.
 *
 * ONE call PER ASSET, never the batched `pricesUSD(address[])`: the lens
 * loops the oracle with no per-item catch, so a single unpriceable asset
 * poisons the whole batch — and FT itself is exactly that on BOTH chains
 * (`priceUSD` reverts). Roster rows with `priceable: false` are skipped
 * entirely (FT is also `collateral: false, mmBps: 0`, so nothing downstream
 * needs this oracle to price it).
 *
 * All entries sit under the single per-chain `FLYING_TULIP` key (cross-margin
 * — the asset segment of the marketUid is the discriminator), joining the
 * lending data emitted by `convertFlyingTulipMarketsToResponse`.
 */

/** Grouped metadata: the lens plus the priceable roster slice, call-ordered. */
interface FlyingTulipChainMeta {
  lendingLens: string
  assets: string[]
}

/** Build the per-asset multicall calls for Flying Tulip oracle prices. */
export function getFlyingTulipCalls(
  chainId: string,
): FetcherResult<FlyingTulipChainMeta>[] {
  const lender = FLYING_TULIP_LENDER_KEY
  const lendingLens = flyingTulipConfigFor(lender, chainId)?.lendingLens
  const roster = flyingTulipAssetsFor(lender, chainId)?.assets ?? []
  const priceable = roster.filter((a) => a.priceable)
  if (!lendingLens || priceable.length === 0) return []

  const assets = priceable.map((a) => a.address.toLowerCase())
  const calls: MulticallCall[] = assets.map((asset) => ({
    address: lendingLens,
    name: 'priceAndDecimals',
    params: [asset],
  }))
  return [{ calls, meta: { lendingLens, assets }, lender }]
}

/**
 * Parse the per-asset `priceAndDecimals` results into direct-USD entries.
 * `data[i]` decodes as `[pxWad, dec]` for `meta.assets[i]`.
 */
export function parseFlyingTulipResults(
  data: any[],
  meta: FlyingTulipChainMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const entries: OraclePriceEntry[] = []
  meta.assets.forEach((asset, i) => {
    const res = data?.[i]
    const pxWad = Array.isArray(res) ? res[0] : undefined
    if (pxWad == null) return
    const priceUSD = Number(pxWad) / 1e18
    if (!(priceUSD > 0)) return
    entries.push({
      asset,
      // Direct USD price — no oracle-unit denomination, so raw == USD.
      price: priceUSD,
      priceUSD,
      marketUid: createMarketUid(chainId, FLYING_TULIP_LENDER_KEY, asset),
      targetLender: FLYING_TULIP_LENDER_KEY,
      description: 'Flying Tulip OracleRouter via LendingLens (USD)',
    })
  })
  return entries
}

/** ABI for the Flying Tulip oracle call (via the LendingLens). */
export function getFlyingTulipAbi() {
  return FlyingTulipLendingLensAbi
}

/** Flying Tulip Oracle Fetcher module. */
export const flyingTulipFetcher = {
  getCalls: getFlyingTulipCalls,
  parse: parseFlyingTulipResults,
  getAbi: getFlyingTulipAbi,
}
